import Button from '@risksmart-app/components/src/button';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_COL_WIDTH } from 'src/App.config';
import { Permission } from 'src/rbac/Permission';

import Link from '@/components/link';
import type { ResolvedRating } from '@/ratings/useRiskRatingResolver';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import type {
  StatefulTableOptions,
  UseGetTablePropsOptions,
} from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetStatelessTableProps } from '@/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from '@/utils/table/hooks/useGetTableProps';
import { exportStyleFromValue } from '@/utils/table/pdfExportStyles';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { addEnterpriseRiskUrl, enterpriseRiskDetailsUrl } from '@/utils/urls';

import type {
  EnterpriseRiskFields,
  EnterpriseRiskRegisterFields,
} from './types';
import { formatDimValue, useLabelledFields } from './useLabelledFields';

/**
 * Badge for an aggregated likelihood/impact dimension. Whole values resolve
 * to a labelled matrix-scale badge; fractional medians (the average of the
 * two middle items) fall back to the bare number.
 */
const dimCell = (
  value: number | null | undefined,
  resolve: (value: number | null | undefined) => ResolvedRating | undefined
) => {
  if (value == null) {
    return '-';
  }
  const resolved = resolve(value);

  return (
    <SimpleRatingBadge rating={resolved ?? { label: formatDimValue(value) }} />
  );
};

export const useGetFieldConfig =
  (): TableFields<EnterpriseRiskRegisterFields> => {
    const { t } = useTranslation(['common'], { keyPrefix: 'columns' });
    const { t: st } = useTranslation(['common'], {
      keyPrefix: 'risks.columns',
    });
    const { t: et } = useTranslation(['common'], {
      keyPrefix: 'enterpriseRisks.columns',
    });
    // Ratings render from the STORED rating values (resolved from
    // likelihood × impact by the aggregation since RSP-6195) — not from a
    // range lookup on the score, which custom risk matrices never match.
    const { getByValue: getResidualRating } = useRating('risk_controlled');
    const { getByValue: getInherentRating } = useRating('risk_uncontrolled');
    const { resolveLikelihood, resolveImpact } = useRiskRatingResolver(
      'standard',
      { configType: 'risk_assessment' }
    );

    return {
      Id: {
        header: t('guid'),
      },
      SequentialIdLabelled: {
        header: t('id'),
        sortingField: 'SequentialId',
      },
      Title: {
        header: t('title'),
        cell: (item) => (
          <Link variant={'secondary'} href={enterpriseRiskDetailsUrl(item.Id)}>
            {item.Title}
          </Link>
        ),
      },
      ParentTitle: {
        header: st('parent_risk'),
      },
      TierLabelled: {
        header: st('risk_tier'),
      },
      TreatmentLabelled: {
        header: st('risk_treatment'),
      },
      Description: {
        header: st('risk_description'),
        maxWidth: MAX_COL_WIDTH,
      },
      InherentMeanLabelled: {
        header: et('inherentRatingMean'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getInherentRating(item.score?.InherentRatingMean)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentRatingMean,
          getInherentRating
        ),
      },
      InherentWorstCaseLabelled: {
        header: et('inherentRatingWorstCase'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getInherentRating(item.score?.InherentRatingWorstCase)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentRatingWorstCase,
          getInherentRating
        ),
      },
      ResidualMeanLabelled: {
        header: et('residualRatingMean'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getResidualRating(item.score?.ResidualRatingMean)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualRatingMean,
          getResidualRating
        ),
      },
      ResidualWorstCaseLabelled: {
        header: et('residualRatingWorstCase'),
        cell: (item) => (
          <SimpleRatingBadge
            rating={getResidualRating(item.score?.ResidualRatingWorstCase)}
          />
        ),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualRatingWorstCase,
          getResidualRating
        ),
      },
      InherentLikelihoodMeanLabelled: {
        header: et('inherentLikelihoodMean'),
        cell: (item) =>
          dimCell(item.score?.InherentLikelihoodMean, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentLikelihoodMean,
          resolveLikelihood
        ),
      },
      InherentImpactMeanLabelled: {
        header: et('inherentImpactMean'),
        cell: (item) => dimCell(item.score?.InherentImpactMean, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentImpactMean,
          resolveImpact
        ),
      },
      ResidualLikelihoodMeanLabelled: {
        header: et('residualLikelihoodMean'),
        cell: (item) =>
          dimCell(item.score?.ResidualLikelihoodMean, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualLikelihoodMean,
          resolveLikelihood
        ),
      },
      ResidualImpactMeanLabelled: {
        header: et('residualImpactMean'),
        cell: (item) => dimCell(item.score?.ResidualImpactMean, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualImpactMean,
          resolveImpact
        ),
      },
      InherentLikelihoodMedianLabelled: {
        header: et('inherentLikelihoodMedian'),
        cell: (item) =>
          dimCell(item.score?.InherentLikelihoodMedian, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentLikelihoodMedian,
          resolveLikelihood
        ),
      },
      InherentImpactMedianLabelled: {
        header: et('inherentImpactMedian'),
        cell: (item) =>
          dimCell(item.score?.InherentImpactMedian, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentImpactMedian,
          resolveImpact
        ),
      },
      ResidualLikelihoodMedianLabelled: {
        header: et('residualLikelihoodMedian'),
        cell: (item) =>
          dimCell(item.score?.ResidualLikelihoodMedian, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualLikelihoodMedian,
          resolveLikelihood
        ),
      },
      ResidualImpactMedianLabelled: {
        header: et('residualImpactMedian'),
        cell: (item) =>
          dimCell(item.score?.ResidualImpactMedian, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualImpactMedian,
          resolveImpact
        ),
      },
      InherentLikelihoodWorstCaseLabelled: {
        header: et('inherentLikelihoodWorstCase'),
        cell: (item) =>
          dimCell(item.score?.InherentLikelihoodWorstCase, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentLikelihoodWorstCase,
          resolveLikelihood
        ),
      },
      InherentImpactWorstCaseLabelled: {
        header: et('inherentImpactWorstCase'),
        cell: (item) =>
          dimCell(item.score?.InherentImpactWorstCase, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.InherentImpactWorstCase,
          resolveImpact
        ),
      },
      ResidualLikelihoodWorstCaseLabelled: {
        header: et('residualLikelihoodWorstCase'),
        cell: (item) =>
          dimCell(item.score?.ResidualLikelihoodWorstCase, resolveLikelihood),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualLikelihoodWorstCase,
          resolveLikelihood
        ),
      },
      ResidualImpactWorstCaseLabelled: {
        header: et('residualImpactWorstCase'),
        cell: (item) =>
          dimCell(item.score?.ResidualImpactWorstCase, resolveImpact),
        exportCellStyle: exportStyleFromValue(
          (item) => item.score?.ResidualImpactWorstCase,
          resolveImpact
        ),
      },
      CreatedAtTimestamp: dateColumnFromConfig({
        header: { header: t('created_on') },
        dateField: 'CreatedAtTimestamp',
      }),
      ModifiedAtTimestamp: dateColumnFromConfig({
        header: { header: t('updated_on') },
        dateField: 'ModifiedAtTimestamp',
      }),
      CreatedByUser: {
        header: t('created_by_id'),
      },
      ModifiedByUser: {
        header: t('updated_by_id'),
      },
    };
  };

export const useGetEnterpriseRiskTableProps = (
  records: EnterpriseRiskFields[] | undefined
): UseGetTablePropsOptions<EnterpriseRiskRegisterFields> => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'enterpriseRisks',
  });
  const data = useLabelledFields(records);
  const fields = useGetFieldConfig();

  return useMemo(() => {
    return {
      tableId: 'enterpriseRiskRegister',
      data,
      fields,
      entityLabel: st('entity_name'),
      emptyCollectionAction: (
        <Permission permission={'insert:enterprise_risk'}>
          <Button href={addEnterpriseRiskUrl()}>{st('createNewButton')}</Button>
        </Permission>
      ),
      enableFiltering: true,
      initialColumns: ['Title', 'ParentTitle', 'TierLabelled'],
      preferencesStorageKey: 'EnterpriseRisksTable-Preferences',
      customAttributeFormIds: ['risk'],
    };
  }, [st, data, fields]);
};

export const useGetCollectionTableProps = (
  records: EnterpriseRiskFields[] | undefined
): TablePropsWithActions<EnterpriseRiskRegisterFields> => {
  const props = useGetEnterpriseRiskTableProps(records);

  return useGetTableProps(props);
};

export const useGetEnterpriseRiskSmartWidgetTableProps = (
  records: EnterpriseRiskFields[] | undefined,
  statefulTableOptions: StatefulTableOptions<EnterpriseRiskRegisterFields>
): TablePropsWithActions<EnterpriseRiskRegisterFields> => {
  const props = useGetEnterpriseRiskTableProps(records);

  return useGetStatelessTableProps<EnterpriseRiskRegisterFields>({
    ...props,
    ...statefulTableOptions,
    enableFiltering: false,
  });
};
